import { useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid } from '@mui/material'

import Button from '../components/Button'
import Select from '../components/Select'
import InputField from '../components/InputField'
import GridHeader from '../components/GridHeader'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetArbitroByIdQuery,
  useGetCorsiQuery,
  useGetImpostazioniContattiQuery,
  useCreateIscrizioneCorsiMutation,
  useUpdateIscrizioneCorsiMutation,
} from '../hooks/api-slice'
import { mailTo, formatBodyField } from '../hooks/utils-functions'
import { config } from '../settings/configs'
import { titlesIscrizione as titles } from '../settings/settings'
import {
  setIscrizioneCorsi,
  setIscrizioneCorsiField,
} from '../container/iscrizioniCorsi'

const IscrizioneCorsi = () => {
  const { admin, arbitro, idArbitro } = useSelector(
    state => state.home.infoUtente,
  )
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const { data: corsi } = useGetCorsiQuery()
  const iscrizioneCorsi = useSelector(
    state => state.iscrizioniCorsi.iscrizioneCorsi,
  )

  const { data: arbitroData } = useGetArbitroByIdQuery(idArbitro, {
    skip: !(arbitro && idArbitro),
  })

  const [createIscrizioneCorsiMutation, { isLoading: isCreating }] =
    useCreateIscrizioneCorsiMutation()
  const [updateIscrizioneCorsiMutation, { isLoading: isUpdating }] =
    useUpdateIscrizioneCorsiMutation()

  const dispatch = useDispatch()
  const { setFeedback } = useFeedback()

  const iDispatch = useCallback(
    (field, value, toCase) =>
      dispatch(
        setIscrizioneCorsiField({
          field,
          value: formatBodyField(value, toCase),
        }),
      ),
    [dispatch],
  )

  const mode = !!iscrizioneCorsi?._id ? 'update' : 'add'

  const docTitle = `${mode === 'add' ? 'Nuova' : 'Modifica'} iscrizione`
  document.title = `${config?.name} - ${docTitle}`

  const isAdd = mode === 'add'
  const iCorsi = corsi?.filter(c => admin || !isAdd || !!c.iscrizioniAperte)
  const isCorsoAperto = corsi?.find(
    x => x._id === iscrizioneCorsi?.idCorso,
  )?.iscrizioniAperte
  const isNotEditable = (!admin && !isAdd && !isCorsoAperto) || arbitro

  // prefill if "arbitro"
  useEffect(() => {
    if (arbitro && arbitroData && isAdd) {
      iDispatch('nome', arbitroData?.nome, 'upper')
      iDispatch('cognome', arbitroData?.cognome, 'upper')
      iDispatch('codiceFiscale', arbitroData?.codiceFiscale, 'upper')
      iDispatch('dataNascita', arbitroData?.dataNascita)
      iDispatch('mail', arbitroData?.mail, 'lower')
      iDispatch('tesseraPGS', arbitroData?.tesseraPGS)
    }
  }, [arbitro, arbitroData, isAdd, iDispatch])

  const addIscrizioneCorsi = async () => {
    let body = iscrizioneCorsi
    if (arbitro) body = { ...body, isArbitro: true }
    const result = await createIscrizioneCorsiMutation(body)
    if (!result?.error)
      setFeedback(titles.titleCompleteAdd, false, '', '/iscrizioni-corsi')
  }

  const updateIscrizioneCorsi = async () => {
    const arg = { id: iscrizioneCorsi?._id, ...iscrizioneCorsi }
    const result = await updateIscrizioneCorsiMutation(arg)
    if (!result?.error)
      setFeedback(titles.titleCompleteUpdate, false, '', '/iscrizioni-corsi')
  }

  const confirmIscrizioneCorsi = async () => {
    if (mode === 'add') await addIscrizioneCorsi()
    if (mode === 'update') await updateIscrizioneCorsi()
  }

  // trigger functions onComponentUnmounts
  useEffect(() => {
    return () => dispatch(setIscrizioneCorsi())
  }, [dispatch])

  const containerProps = {
    container: true,
    paddingY: 2,
    rowSpacing: 2,
    columnSpacing: 5,
    sx: { width: '100%' },
  }

  return (
    <Grid container paddingX={2} sx={{ width: '100%' }}>
      <GridHeader h2 text={docTitle} />
      {!admin && (
        <Grid size={12}>
          <center>
            <p>
              Per domande sulle iscrizioni ai corsi scrivere a{' '}
              {mailTo(contatti?.MailSegreteria)}.
            </p>
          </center>
        </Grid>
      )}
      <Grid {...containerProps}>
        <Grid size={12}>
          <Select
            id='corso'
            value={iscrizioneCorsi?.idCorso}
            options={iCorsi?.map(c => c._id)}
            optionLabels={iCorsi?.map(
              c =>
                `${c.nome}${c.date?.length ? ` - ${c.date?.join(', ')}` : ''}${
                  c.indirizzo ? ` - ${c.indirizzo}` : ''
                }`,
            )}
            label='Corso *'
            onChange={i => iDispatch('idCorso', i)}
            errorFunc={() => !iscrizioneCorsi?.idCorso}
            mode={mode}
            readOnly={!isAdd}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='nome'
            value={iscrizioneCorsi?.nome}
            onChange={i => iDispatch('nome', i, 'upper')}
            label='Nome *'
            errorFunc={() => !iscrizioneCorsi?.nome}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='cognome'
            value={iscrizioneCorsi?.cognome}
            onChange={i => iDispatch('cognome', i, 'upper')}
            label='Cognome *'
            errorFunc={() => !iscrizioneCorsi?.cognome}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='codice-fiscale'
            value={iscrizioneCorsi?.codiceFiscale}
            onChange={i =>
              iDispatch('codiceFiscale', i.replace(/\W/g, ''), 'upper')
            }
            label='Codice fiscale'
            errorFunc={() => !iscrizioneCorsi?.codiceFiscale}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='data-nascita'
            value={iscrizioneCorsi?.dataNascita}
            onChange={i => iDispatch('dataNascita', i)}
            label='Data di nascita'
            errorFunc={() => !iscrizioneCorsi?.dataNascita}
            adornment='date'
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='mail'
            value={iscrizioneCorsi?.mail}
            onChange={i => iDispatch('mail', i?.replace(/\s/g, ''), 'lower')}
            label='E-mail'
            type='email'
            adornment='email'
            errorFunc={() => !iscrizioneCorsi?.mail}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <InputField
            id='tessera'
            value={iscrizioneCorsi?.tesseraPGS}
            onChange={i => iDispatch('tesseraPGS', i)}
            label='Tessera PGS'
            errorFunc={() => !iscrizioneCorsi?.tesseraPGS}
            mode={mode}
            readOnly={isNotEditable}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <Grid size='grow'>
          <center>
            <Button error to='/iscrizioni-corsi' children='Indietro' />
          </center>
        </Grid>
        <Grid size='grow'>
          <center>
            <Button
              dark
              loading={isCreating || isUpdating}
              onClick={confirmIscrizioneCorsi}
            >
              Salva
            </Button>
          </center>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default IscrizioneCorsi
